import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  // FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  // Radio,
  // RadioGroup,
  TextField,
} from "@mui/material";
import axios from "axios";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import http from "../../utils/http";
import { errorToastMessage } from "../../utils/toast";
import {
  AuthLayout,
  //  AuthRadioSelector,
  SignInWrapper,
} from "./auth.style";
import { StyledButton } from "../Common/styles/button";
import ForceLogoutModal from "./ForceLogoutModal";
import { useAppDispatch } from "../../Redux/hooks";
import { establishSession } from "../../utils/establishSession";
import { InputWrapper, LabelStyle } from "../Common/styles/form";
import isEmail from "validator/es/lib/isEmail";
import EmbraceLogo from "../Common/EmbraceLogo";
import { useQuery } from "../../utils/hooks";
import { useTranslation } from "react-i18next";

const schema = yup.object({
  email: yup
    .string()
    .required("LOGIN_PAGE.VALIDATION_EMAIL_REQUIRED")
    .test("is-valid", "LOGIN_PAGE.VALIDATION_EMAIL_INVALID", (value) =>
      value ? isEmail(value) : false
    ),
  password: yup
    .string()
    .min(6, "LOGIN_PAGE.VALIDATION_PASSWORD_SHORT")
    .required("LOGIN_PAGE.VALIDATION_PASSWORD_REQUIRED"),
});

const SignIn = () => {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const emailverified = useQuery().get("email_invite");
  const [submitLoader, setSubmitLoader] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [logoutData, setLogoutData] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useAppDispatch();
  const closeModal = () => {
    setShowModal(false);
  };

  const openModal = (data: any) => {
    setLogoutData(data);
    setShowModal(true);
  };

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (data: any) => {
    try {
      setSubmitLoader(true);
      const body: any = {
        username: data.email,
        password: data.password,
        userAgent: window.navigator.userAgent,
        strategy: "email",
      };
      let url = "user/auth/login";
      const res = await http.post(url, body);
      setSubmitLoader(true);
      if (res?.data?.data?.isMfaEnabled) {
        sessionStorage.setItem("otp-token", res?.data?.data?.token);
        navigate("/auth/2fa");
      } else {
        establishSession(dispatch, {
          accessToken: res.data.data.accessToken,
          refreshToken: res.data.data.refreshToken,
          id: res.data.data.id,
          role: res.data.data.role,
          firstName: res?.data?.data?.firstName,
          lastName: res?.data?.data?.lastName,
        });
      }
    } catch (err) {
      setSubmitLoader(false);
      if (axios.isAxiosError(err) && err?.response?.status === 409) {
        openModal(data);
      } else {
        errorToastMessage(err as Error);
      }
    }
  };

  return (
    <Box sx={AuthLayout}>
      <Box sx={SignInWrapper}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 5 }}>
          <EmbraceLogo />
        </Box>
        {emailverified &&
          (emailverified === "success" ? (
            <Alert
              severity="success"
              sx={{
                mb: 2,
              }}
            >
              {t("LOGIN_PAGE.INVITE_SUCCESS")}
            </Alert>
          ) : emailverified === "failed" ? (
            <Alert
              severity="error"
              sx={{
                mb: 2,
              }}
            >
              {t("LOGIN_PAGE.INVITE_FAILED")}
            </Alert>
          ) : null)}
        <Formik
          initialValues={{
            email: "",
            password: "",
          }}
          onSubmit={handleSubmit}
          validationSchema={schema}
        >
          {({ errors, touched, getFieldProps }) => {
            return (
              <Form>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="email">
                    {t("LOGIN_PAGE.EMAIL_LABEL")}
                  </FormLabel>
                  <TextField
                    placeholder={t("LOGIN_PAGE.EMAIL_PLACEHOLDER")}
                    id="email"
                    {...getFieldProps("email")}
                    error={touched?.email && errors?.email ? true : false}
                    helperText={
                      touched?.email && errors?.email
                        ? t(errors?.email as string)
                        : " "
                    }
                  />
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="password">
                    {t("LOGIN_PAGE.PASSWORD_LABEL")}
                  </FormLabel>
                  <TextField
                    placeholder={t("LOGIN_PAGE.PASSWORD_PLACEHOLDER")}
                    id="password"
                    {...getFieldProps("password")}
                    error={touched?.password && errors?.password ? true : false}
                    helperText={
                      touched?.password && errors?.password
                        ? t(errors?.password as string)
                        : " "
                    }
                    type={showPassword ? "text" : "password"}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            onClick={handleClickShowPassword}
                            edge="end"
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
                {!submitLoader ? (
                  <StyledButton variant="contained" type="submit" fullWidth>
                    {t("LOGIN_PAGE.SIGN_IN_BUTTON")}
                  </StyledButton>
                ) : (
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={25} />
                  </Box>
                )}
                <Box sx={{ textAlign: "center", mt: 2 }}>
                  <NavLink to="/auth/forgot-password">
                    {t("LOGIN_PAGE.FORGOT_PASSWORD")}
                  </NavLink>
                </Box>
                {/* <Box sx={{ textAlign: "center", mt: 2 }}>
                  {t("LOGIN_PAGE.NOT_ADMIN")}{" "}
                  <NavLink to="/auth/participant/login">
                    {t("LOGIN_PAGE.CLICK_HERE")}
                  </NavLink>
                  <span> {t("LOGIN_PAGE.LOGIN_AS_PARTICIPANT")}</span>
                </Box> */}
              </Form>
            );
          }}
        </Formik>
      </Box>
      {showModal && (
        <ForceLogoutModal
          showModal={showModal}
          closeModal={closeModal}
          logoutData={logoutData}
        />
      )}
    </Box>
  );
};

export default SignIn;
